/**
 * PrivacyPolicy.jsx — Privacy Policy page
 *
 * Accessible at /privacy. Required for Twilio A2P 10DLC registration
 * and general GDPR/CCPA best practice.
 */
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'April 23, 2026';
const CONTACT_EMAIL = 'hello@virisclayworks.com';
const SITE_URL = 'virisclayworks.com';

export default function PrivacyPolicy() {
  return (
    <div
      className="fade"
      style={{ padding: '32px 48px 64px', maxWidth: 860, margin: '0 auto' }}
    >
      {/* Header */}
      <div
        className="tile"
        style={{ padding: '40px 48px 36px', marginBottom: 24 }}
      >
        <p style={s.eyebrow}>Viri&apos;s Clayworks</p>
        <h1 style={s.heading}>Privacy Policy</h1>
        <p style={s.sub}>Effective date: {EFFECTIVE_DATE}</p>
      </div>

      {/* Body */}
      <div className="tile" style={{ padding: '40px 48px', lineHeight: 1.8 }}>
        <p style={s.body}>
          Viri&apos;s Clayworks (&quot;we,&quot; &quot;us,&quot; or
          &quot;our&quot;) operates {SITE_URL}. This Privacy Policy explains how
          we collect, use, and protect your personal information when you visit
          our website or book a pottery class with us.
        </p>

        <Section title="1. Information We Collect">
          <p style={s.body}>
            We collect information you provide directly, including:
          </p>
          <ul style={s.list}>
            <li>
              <strong>Contact information</strong> — name, email address, and
              phone number when you make a booking or join our newsletter.
            </li>
            <li>
              <strong>Booking details</strong> — class selections, session
              dates, number of seats, and payment status.
            </li>
            <li>
              <strong>Payment information</strong> — processed securely through
              Square. We do not store full card numbers.
            </li>
            <li>
              <strong>Communications</strong> — messages you send us via email
              or through contact forms.
            </li>
          </ul>
          <p style={s.body}>
            We also collect limited technical data automatically (browser type,
            pages visited, referral source) via Firebase Analytics for site
            performance purposes.
          </p>
        </Section>

        <Section title="2. How We Use Your Information">
          <ul style={s.list}>
            <li>To process and confirm your class bookings.</li>
            <li>
              To send transactional SMS and email notifications about your
              booking (confirmation, reminders, updates, cancellations, and
              pickup notifications).
            </li>
            <li>
              To send our newsletter if you have subscribed — you can
              unsubscribe at any time.
            </li>
            <li>
              To sync your contact information with our Square CRM for customer
              management.
            </li>
            <li>To respond to your inquiries and provide customer support.</li>
            <li>To improve our website and services.</li>
          </ul>
        </Section>

        <Section title="3. SMS Notifications">
          <p style={s.body}>
            When you provide your phone number and check the SMS consent box
            during booking or waitlist signup, you agree to receive
            transactional SMS messages from Viri&apos;s Clayworks. These
            messages include booking confirmations, class reminders, booking
            changes, cancellations, and pickup notifications.
          </p>
          <ul style={s.list}>
            <li>
              <strong>Message frequency:</strong> Varies based on your booking
              activity — typically 1–5 messages per booking (confirmation,
              reminder, and any updates).
            </li>
            <li>
              <strong>Rates:</strong> Message and data rates may apply.
            </li>
            <li>
              <strong>Opt-out:</strong> Reply <strong>STOP</strong> to any
              message to unsubscribe immediately. Reply <strong>HELP</strong>{' '}
              for assistance.
            </li>
            <li>
              <strong>No sharing:</strong> Your mobile phone number is never
              sold, rented, or shared with third parties for marketing or
              promotional purposes.
            </li>
            <li>
              <strong>Marketing SMS:</strong> We do not send promotional or
              marketing SMS messages. All SMS messages are transactional and
              directly related to a booking you have made.
            </li>
          </ul>
        </Section>

        <Section title="4. Email Newsletter">
          <p style={s.body}>
            If you subscribe to our email newsletter, we collect your email
            address and use it to send you class announcements, studio news, and
            promotions.
          </p>
          <ul style={s.list}>
            <li>
              <strong>Opt-in:</strong> You subscribe voluntarily by submitting
              your email in the newsletter sign-up form on our website.
            </li>
            <li>
              <strong>Opt-out:</strong> Every newsletter includes an{' '}
              <strong>Unsubscribe</strong> link at the bottom. Clicking it
              removes you from our mailing list promptly. You can also email us
              at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} style={s.link}>
                {CONTACT_EMAIL}
              </a>{' '}
              to be removed.
            </li>
            <li>
              <strong>No sharing:</strong> We do not sell or share your email
              address with third parties for marketing purposes.
            </li>
          </ul>
        </Section>

        <Section title="5. How We Share Your Information">
          <p style={s.body}>
            We do not sell or rent your personal information. We do not share
            your mobile phone number with third parties for marketing or
            promotional purposes. We share your information only with trusted
            service providers necessary to operate our business:
          </p>
          <ul style={s.list}>
            <li>
              <strong>Square</strong> — payment processing and customer CRM.
            </li>
            <li>
              <strong>Twilio</strong> — SMS notification delivery.
            </li>
            <li>
              <strong>Resend</strong> — transactional email delivery.
            </li>
            <li>
              <strong>Google Firebase</strong> — database, hosting, and
              analytics.
            </li>
          </ul>
          <p style={s.body}>
            Each provider has its own privacy policy and is contractually
            required to protect your data.
          </p>
        </Section>

        <Section title="6. Data Retention">
          <p style={s.body}>
            We retain booking and customer records for as long as necessary to
            provide our services and comply with legal obligations. You may
            request deletion of your personal data at any time by contacting us
            at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={s.link}>
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>

        <Section title="7. Cookies">
          <p style={s.body}>
            Our site uses essential cookies for functionality and Firebase
            Analytics cookies to understand how visitors use our site. No
            third-party advertising cookies are used.
          </p>
        </Section>

        <Section title="8. Your Rights">
          <p style={s.body}>
            Depending on your location, you may have the right to access,
            correct, or delete your personal information. To exercise these
            rights, contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={s.link}>
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>

        <Section title="9. Children's Privacy">
          <p style={s.body}>
            Our services are not directed to children under 13. We do not
            knowingly collect personal information from children.
          </p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p style={s.body}>
            We may update this Privacy Policy from time to time. Changes will be
            posted on this page with an updated effective date.
          </p>
        </Section>

        <Section title="11. Contact Us">
          <p style={s.body}>
            If you have questions about this Privacy Policy, please contact us:
          </p>
          <p style={{ ...s.body, marginTop: 8 }}>
            <strong>Viri&apos;s Clayworks</strong>
            <br />
            <a href={`mailto:${CONTACT_EMAIL}`} style={s.link}>
              {CONTACT_EMAIL}
            </a>
            <br />
            {SITE_URL}
          </p>
        </Section>

        <div
          style={{
            borderTop: '1px solid #E8E0CC',
            marginTop: 40,
            paddingTop: 24,
          }}
        >
          <Link to="/terms" style={s.link}>
            Terms &amp; Conditions →
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={s.sectionTitle}>{title}</h2>
      {children}
    </div>
  );
}

const s = {
  eyebrow: {
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    fontSize: '0.65rem',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: '#9C7A3C',
    margin: '0 0 10px',
  },
  heading: {
    fontFamily: 'Georgia, serif',
    fontWeight: 400,
    fontSize: '2rem',
    color: '#1a1208',
    margin: '0 0 8px',
  },
  sub: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.82rem',
    color: '#9C7A3C',
    margin: 0,
  },
  sectionTitle: {
    fontFamily: 'Georgia, serif',
    fontWeight: 400,
    fontSize: '1.1rem',
    color: '#1a1208',
    margin: '0 0 10px',
  },
  body: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.92rem',
    color: '#3a2e1e',
    margin: '0 0 12px',
  },
  list: {
    fontFamily: "'Raleway', sans-serif",
    fontSize: '0.92rem',
    color: '#3a2e1e',
    margin: '0 0 12px',
    paddingLeft: 24,
    lineHeight: 1.9,
  },
  link: {
    color: '#9C7A3C',
    textDecoration: 'underline',
  },
};
